"use client";

import { useState, useEffect, useRef, useCallback } from "react";

interface PanelSize {
  width: number;
  height: number;
}

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

const STORAGE_KEY = "superone_phone_screen_panel_size";
const POSITION_STORAGE_KEY = "superone_phone_screen_panel_position";
const MIN_WIDTH = 280;
const MIN_HEIGHT = 400;
const MAX_WIDTH_RATIO = 0.6;
const MAX_HEIGHT_RATIO = 0.9;

// API 地址
const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

export function PhoneScreenPanel({ onClose }: { onClose: () => void }) {
  const [screenUrl, setScreenUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000); // 默认5秒刷新
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [deviceStatus, setDeviceStatus] = useState<"connected" | "disconnected" | "unknown">("unknown");

  // 交互状态
  const [interactionMode, setInteractionMode] = useState<"view" | "interact">("view");
  const [isOperating, setIsOperating] = useState(false);
  const [operationFeedback, setOperationFeedback] = useState<string | null>(null);
  const [hoverCoords, setHoverCoords] = useState<{ x: number; y: number } | null>(null);

  // 触摸/滑动状态
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number; naturalWidth: number; naturalHeight: number } | null>(null);

  // 手机屏幕真实分辨率（用于坐标转换）
  const [phoneScreenSize, setPhoneScreenSize] = useState<{ width: number; height: number } | null>(null);

  // 面板大小和拖拽
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelSize, setPanelSize] = useState<PanelSize>({ width: 320, height: 600 });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, width: 0, height: 0 });

  // 面板位置拖动
  const [panelPosition, setPanelPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDraggingPanel, setIsDraggingPanel] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 });

  // 加载保存的面板大小和位置
  useEffect(() => {
    const savedSize = localStorage.getItem(STORAGE_KEY);
    if (savedSize) {
      try {
        const size = JSON.parse(savedSize);
        const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
        const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
        setPanelSize({
          width: Math.min(Math.max(size.width, MIN_WIDTH), maxWidth),
          height: Math.min(Math.max(size.height, MIN_HEIGHT), maxHeight)
        });
      } catch {
        // ignore
      }
    }

    const savedPosition = localStorage.getItem(POSITION_STORAGE_KEY);
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        // 确保位置在屏幕范围内
        const maxX = window.innerWidth - 100;
        const maxY = window.innerHeight - 100;
        setPanelPosition({
          x: Math.min(Math.max(pos.x, 0), maxX),
          y: Math.min(Math.max(pos.y, 0), maxY)
        });
      } catch {
        // ignore
      }
    }
  }, []);

  // 保存面板大小
  const savePanelSize = useCallback((size: PanelSize) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(size));
  }, []);

  // 保存面板位置
  const savePanelPosition = useCallback((pos: { x: number; y: number }) => {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(pos));
  }, []);

  // 拖拽调整大小
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: panelSize.width,
      height: panelSize.height
    };
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;

      const maxWidth = window.innerWidth * MAX_WIDTH_RATIO;
      const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;

      const newWidth = Math.min(Math.max(resizeStartRef.current.width + deltaX, MIN_WIDTH), maxWidth);
      const newHeight = Math.min(Math.max(resizeStartRef.current.height + deltaY, MIN_HEIGHT), maxHeight);

      setPanelSize({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      savePanelSize(panelSize);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, panelSize, savePanelSize]);

  // 拖动面板位置
  const handleDragStart = (e: React.MouseEvent) => {
    // 只在标题栏区域拖动
    e.preventDefault();
    setIsDraggingPanel(true);
    const currentPos = panelPosition || { x: 80, y: window.innerHeight - 600 - 64 };
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: currentPos.x,
      posY: currentPos.y
    };
  };

  useEffect(() => {
    if (!isDraggingPanel) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;

      const newX = dragStartRef.current.posX + deltaX;
      const newY = dragStartRef.current.posY + deltaY;

      // 限制在屏幕范围内
      const maxX = window.innerWidth - panelSize.width;
      const maxY = window.innerHeight - panelSize.height;

      setPanelPosition({
        x: Math.min(Math.max(newX, 0), maxX),
        y: Math.min(Math.max(newY, 0), maxY)
      });
    };

    const handleMouseUp = () => {
      setIsDraggingPanel(false);
      if (panelPosition) {
        savePanelPosition(panelPosition);
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDraggingPanel, panelPosition, panelSize, savePanelPosition]);

  // 截取屏幕
  const captureScreen = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await fetch(`${API_BASE}/api/phone/screenshot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "截图失败");
      }

      const data = await res.json();

      if (data.image_url) {
        setScreenUrl(data.image_url + "?t=" + Date.now());
        setDeviceStatus("connected");
        setLastUpdate(new Date());
        // 保存手机屏幕真实分辨率
        if (data.screen_width && data.screen_height) {
          setPhoneScreenSize({ width: data.screen_width, height: data.screen_height });
        }
      } else {
        throw new Error("未获取到截图");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "截图失败");
      setDeviceStatus("disconnected");
    } finally {
      setLoading(false);
    }
  }, []);

  // 初始加载和自动刷新
  useEffect(() => {
    captureScreen();
  }, [captureScreen]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(captureScreen, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, captureScreen]);

  // 切换刷新间隔
  const cycleRefreshInterval = () => {
    const intervals = [5000, 8000, 10000, 15000]; // 5s/8s/10s/15s
    const currentIndex = intervals.indexOf(refreshInterval);
    const nextIndex = (currentIndex + 1) % intervals.length;
    setRefreshInterval(intervals[nextIndex]);
  };

  // 更新图片尺寸信息
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setImageSize({
      width: img.clientWidth,
      height: img.clientHeight,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight
    });
  };

  // 将屏幕坐标转换为手机坐标
  const convertToPhoneCoords = (clientX: number, clientY: number): { x: number; y: number } | null => {
    if (!imageRef.current || !imageSize) return null;

    const rect = imageRef.current.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;

    // 检查是否在图片范围内
    if (relX < 0 || relY < 0 || relX > imageSize.width || relY > imageSize.height) {
      return null;
    }

    // 使用手机屏幕真实分辨率进行坐标转换（如果可用）
    // 否则回退到图片的 naturalWidth/naturalHeight
    const targetWidth = phoneScreenSize?.width || imageSize.naturalWidth;
    const targetHeight = phoneScreenSize?.height || imageSize.naturalHeight;

    // 转换为手机屏幕坐标
    const phoneX = Math.round((relX / imageSize.width) * targetWidth);
    const phoneY = Math.round((relY / imageSize.height) * targetHeight);

    return { x: phoneX, y: phoneY };
  };

  // 显示操作反馈
  const showFeedback = (message: string) => {
    setOperationFeedback(message);
    setTimeout(() => setOperationFeedback(null), 1500);
  };

  // 点击操作
  const handleTap = async (x: number, y: number) => {
    setIsOperating(true);
    try {
      const res = await fetch(`${API_BASE}/api/phone/tap`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x, y })
      });

      if (res.ok) {
        showFeedback(`点击 (${x}, ${y})`);
        // 操作后刷新屏幕
        setTimeout(captureScreen, 300);
      } else {
        const data = await res.json().catch(() => ({}));
        showFeedback(`失败: ${data.detail || "点击失败"}`);
      }
    } catch (err) {
      showFeedback("点击失败");
    } finally {
      setIsOperating(false);
    }
  };

  // 滑动操作
  const handleSwipe = async (x1: number, y1: number, x2: number, y2: number) => {
    setIsOperating(true);
    try {
      const res = await fetch(`${API_BASE}/api/phone/swipe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ x1, y1, x2, y2, duration: 300 })
      });

      if (res.ok) {
        showFeedback(`滑动`);
        setTimeout(captureScreen, 500);
      } else {
        const data = await res.json().catch(() => ({}));
        showFeedback(`失败: ${data.detail || "滑动失败"}`);
      }
    } catch (err) {
      showFeedback("滑动失败");
    } finally {
      setIsOperating(false);
    }
  };

  // 返回键
  const handleBack = async () => {
    setIsOperating(true);
    try {
      const res = await fetch(`${API_BASE}/api/phone/back`, {
        method: "POST"
      });

      if (res.ok) {
        showFeedback("返回");
        setTimeout(captureScreen, 300);
      } else {
        showFeedback("返回失败");
      }
    } catch (err) {
      showFeedback("返回失败");
    } finally {
      setIsOperating(false);
    }
  };

  // HOME 键
  const handleHome = async () => {
    setIsOperating(true);
    try {
      const res = await fetch(`${API_BASE}/api/phone/home`, {
        method: "POST"
      });

      if (res.ok) {
        showFeedback("回到桌面");
        setTimeout(captureScreen, 300);
      } else {
        showFeedback("操作失败");
      }
    } catch (err) {
      showFeedback("操作失败");
    } finally {
      setIsOperating(false);
    }
  };

  // 鼠标按下
  const handleMouseDown = (e: React.MouseEvent) => {
    if (interactionMode !== "interact" || isOperating) return;

    const coords = convertToPhoneCoords(e.clientX, e.clientY);
    if (!coords) return;

    setTouchStart({ x: coords.x, y: coords.y, time: Date.now() });
    setIsDragging(false);
  };

  // 鼠标移动
  const handleMouseMove = (e: React.MouseEvent) => {
    // 在交互模式下始终更新坐标显示
    if (interactionMode === "interact") {
      const coords = convertToPhoneCoords(e.clientX, e.clientY);
      setHoverCoords(coords);
    }

    // 不再在这里设置 isDragging，由 handleMouseUp 统一判断
  };

  // 鼠标离开图片区域
  const handleMouseLeaveImage = () => {
    setTouchStart(null);
    setIsDragging(false);
    setHoverCoords(null);
  };

  // 鼠标抬起
  const handleMouseUp = (e: React.MouseEvent) => {
    if (!touchStart || interactionMode !== "interact" || isOperating) return;

    const coords = convertToPhoneCoords(e.clientX, e.clientY);
    if (!coords) {
      setTouchStart(null);
      setIsDragging(false);
      return;
    }

    const dx = Math.abs(coords.x - touchStart.x);
    const dy = Math.abs(coords.y - touchStart.y);

    // 移动距离 > 30px 才算滑动，否则算点击
    if (dx > 30 || dy > 30) {
      // 滑动
      handleSwipe(touchStart.x, touchStart.y, coords.x, coords.y);
    } else {
      // 点击（使用起始坐标，更精确）
      handleTap(touchStart.x, touchStart.y);
    }

    setTouchStart(null);
    setIsDragging(false);
  };

  // 触摸开始（移动端）
  const handleTouchStart = (e: React.TouchEvent) => {
    if (interactionMode !== "interact" || isOperating) return;

    // 阻止默认行为，防止页面滚动
    e.preventDefault();

    const touch = e.touches[0];
    const coords = convertToPhoneCoords(touch.clientX, touch.clientY);
    if (!coords) return;

    setTouchStart({ x: coords.x, y: coords.y, time: Date.now() });
    setIsDragging(false);
  };

  // 触摸移动（移动端）
  const handleTouchMove = (e: React.TouchEvent) => {
    if (interactionMode !== "interact") return;

    // 阻止默认行为，防止页面滚动
    e.preventDefault();

    const touch = e.touches[0];
    const coords = convertToPhoneCoords(touch.clientX, touch.clientY);
    setHoverCoords(coords);
  };

  // 触摸结束（移动端）
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || interactionMode !== "interact" || isOperating) return;

    // 阻止默认行为
    e.preventDefault();

    // 使用 changedTouches 获取结束时的触摸点
    const touch = e.changedTouches[0];
    const coords = convertToPhoneCoords(touch.clientX, touch.clientY);
    if (!coords) {
      setTouchStart(null);
      setIsDragging(false);
      return;
    }

    const dx = Math.abs(coords.x - touchStart.x);
    const dy = Math.abs(coords.y - touchStart.y);

    // 移动距离 > 30px 才算滑动，否则算点击
    if (dx > 30 || dy > 30) {
      // 滑动
      handleSwipe(touchStart.x, touchStart.y, coords.x, coords.y);
    } else {
      // 点击（使用起始坐标，更精确）
      handleTap(touchStart.x, touchStart.y);
    }

    setTouchStart(null);
    setIsDragging(false);
    setHoverCoords(null);
  };

  return (
    <>
      {/* Backdrop - 不点击关闭，只作为背景 */}
      <div
        className="fixed inset-0 z-40 bg-black/20 pointer-events-none"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed rounded-lg shadow-2xl z-50 flex flex-col overflow-hidden select-none"
        style={{
          backgroundColor: "#1e1e1e",
          border: "1px solid #3a3a3a",
          width: panelSize.width,
          height: panelSize.height,
          left: panelPosition ? panelPosition.x : 80,
          top: panelPosition ? panelPosition.y : undefined,
          bottom: panelPosition ? undefined : 64
        }}
      >
        {/* Header - 可拖动区域 */}
        <div
          className="flex items-center justify-between px-3 py-2 border-b border-[#3a3a3a] flex-shrink-0"
          style={{ cursor: isDraggingPanel ? "grabbing" : "grab" }}
          onMouseDown={handleDragStart}
        >
          <div className="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="#10b981"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
              />
            </svg>
            <span className="text-[#e5e5e5] font-medium text-sm">手机屏幕</span>
            <span
              className={`w-2 h-2 rounded-full ${
                deviceStatus === "connected"
                  ? "bg-green-500"
                  : deviceStatus === "disconnected"
                  ? "bg-red-500"
                  : "bg-yellow-500"
              }`}
              title={
                deviceStatus === "connected"
                  ? "已连接"
                  : deviceStatus === "disconnected"
                  ? "未连接"
                  : "检测中"
              }
            />
          </div>
          <div className="flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>
            {/* 交互模式切换 */}
            <button
              onClick={() => setInteractionMode(interactionMode === "view" ? "interact" : "view")}
              className={`p-1.5 rounded transition-colors ${
                interactionMode === "interact"
                  ? "bg-[#f97316]/20 text-[#f97316]"
                  : "text-[#606060] hover:bg-[#3a3a3a] hover:text-[#e5e5e5]"
              }`}
              title={interactionMode === "interact" ? "交互模式（点击切换为查看模式）" : "查看模式（点击切换为交互模式）"}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                />
              </svg>
            </button>
            {/* 刷新间隔按钮 */}
            <button
              onClick={cycleRefreshInterval}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                autoRefresh
                  ? "bg-[#2a2a2a] text-[#10b981]"
                  : "text-[#606060]"
              }`}
              title="点击切换刷新间隔"
            >
              {refreshInterval / 1000}s
            </button>
            {/* 自动刷新切换 */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-1.5 rounded transition-colors ${
                autoRefresh
                  ? "bg-[#10b981]/20 text-[#10b981]"
                  : "text-[#606060] hover:bg-[#3a3a3a] hover:text-[#e5e5e5]"
              }`}
              title={autoRefresh ? "暂停自动刷新" : "开启自动刷新"}
            >
              {autoRefresh ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </button>
            {/* 手动刷新 */}
            <button
              onClick={captureScreen}
              disabled={loading}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5] disabled:opacity-50"
              title="立即刷新"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
            {/* 关闭按钮 */}
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5]"
              title="关闭"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content - 屏幕显示区域 */}
        <div className="flex-1 flex flex-col overflow-hidden bg-black relative">
          {/* 操作反馈提示 */}
          {operationFeedback && (
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10 px-3 py-1 bg-black/80 text-white text-xs rounded-full">
              {operationFeedback}
            </div>
          )}

          {/* 交互模式提示 */}
          {interactionMode === "interact" && !error && screenUrl && (
            <div className="absolute top-2 right-2 z-10 px-2 py-1 bg-[#f97316]/90 text-white text-[10px] rounded">
              交互模式
            </div>
          )}

          {error ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto mb-3 text-red-500/50"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p className="text-red-400 text-sm mb-2">{error}</p>
                <p className="text-[#606060] text-xs mb-3">请确保手机已通过 USB 连接并开启调试模式</p>
                <button
                  onClick={captureScreen}
                  className="px-3 py-1.5 text-xs bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#e5e5e5] rounded transition-colors"
                >
                  重试
                </button>
              </div>
            </div>
          ) : screenUrl ? (
            <div
              className="flex-1 flex items-center justify-center p-2 overflow-hidden"
              style={{ cursor: interactionMode === "interact" ? "crosshair" : "default" }}
            >
              <img
                ref={imageRef}
                src={screenUrl}
                alt="手机屏幕"
                className="max-w-full max-h-full object-contain rounded"
                style={{ imageRendering: "auto" }}
                onLoad={handleImageLoad}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeaveImage}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                draggable={false}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-12 w-12 mx-auto mb-3 text-[#606060] animate-pulse"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-[#606060] text-sm">正在连接设备...</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - 操作按钮栏 */}
        <div className="px-3 py-2 border-t border-[#3a3a3a] flex items-center justify-between flex-shrink-0">
          {/* 左侧：操作按钮 */}
          <div className="flex items-center gap-2">
            {/* 返回按钮 */}
            <button
              onClick={handleBack}
              disabled={isOperating || deviceStatus !== "connected"}
              className="p-2 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5] disabled:opacity-50 disabled:cursor-not-allowed"
              title="返回"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            {/* HOME 按钮 */}
            <button
              onClick={handleHome}
              disabled={isOperating || deviceStatus !== "connected"}
              className="p-2 rounded hover:bg-[#3a3a3a] transition-colors text-[#808080] hover:text-[#e5e5e5] disabled:opacity-50 disabled:cursor-not-allowed"
              title="回到桌面"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </button>
          </div>

          {/* 中间：实时坐标（交互模式下显示） */}
          {interactionMode === "interact" && hoverCoords && (
            <div className="text-[11px] text-[#f97316] font-mono">
              ({hoverCoords.x}, {hoverCoords.y})
            </div>
          )}

          {/* 右侧：状态信息 */}
          <div className="text-[10px] text-[#606060]">
            {lastUpdate
              ? `${lastUpdate.toLocaleTimeString("zh-CN")}`
              : "等待连接"}
            {autoRefresh && ` · ${refreshInterval / 1000}s`}
          </div>
        </div>

        {/* 拖拽调整大小的手柄 */}
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeStart}
          style={{
            background: "linear-gradient(135deg, transparent 50%, #4a4a4a 50%)"
          }}
        />
      </div>
    </>
  );
}
